import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Pause, Play, Trash2, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BrowserRecorderProps {
  onRecordingComplete: (blob: Blob) => Promise<void>;
  isUploading?: boolean;
  disabled?: boolean;
}

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

export function BrowserRecorder({
  onRecordingComplete,
  isUploading = false,
  disabled = false,
}: BrowserRecorderProps) {
  const [state, setState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      // Setup audio analyzer for visualization
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      source.connect(analyzer);
      analyzerRef.current = analyzer;

      // Start level monitoring
      const updateLevel = () => {
        if (analyzerRef.current && state === 'recording') {
          const dataArray = new Uint8Array(analyzerRef.current.frequencyBinCount);
          analyzerRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAudioLevel(average / 255);
          animationRef.current = requestAnimationFrame(updateLevel);
        }
      };

      // Create MediaRecorder
      const options = { mimeType: 'audio/webm;codecs=opus' };
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));

        // Cleanup
        stream.getTracks().forEach(track => track.stop());
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };

      recorder.start(1000); // Collect data every second
      setState('recording');
      setDuration(0);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

      // Start level monitoring
      updateLevel();
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to access microphone. Please check your browser permissions.'
      );
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.pause();
      setState('paused');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, [state]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');
      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
  }, [state]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && (state === 'recording' || state === 'paused')) {
      mediaRecorderRef.current.stop();
      setState('stopped');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setAudioLevel(0);
    }
  }, [state]);

  const discardRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setState('idle');
    chunksRef.current = [];
  }, [audioUrl]);

  const handleUpload = async () => {
    if (audioBlob) {
      await onRecordingComplete(audioBlob);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Recording Controls */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
        <div className="flex flex-col items-center space-y-4">
          {/* Audio Level Visualization */}
          {(state === 'recording' || state === 'paused') && (
            <div className="flex items-center justify-center space-x-1 h-12">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'w-1 rounded-full transition-all duration-75',
                    state === 'recording'
                      ? 'bg-red-500'
                      : 'bg-gray-400'
                  )}
                  style={{
                    height: state === 'recording'
                      ? `${Math.max(8, audioLevel * 48 * (1 + Math.sin(i * 0.5) * 0.3))}px`
                      : '8px',
                  }}
                />
              ))}
            </div>
          )}

          {/* Duration Display */}
          <div className={cn(
            'text-3xl font-mono font-bold',
            state === 'recording' && 'text-red-500',
            state === 'paused' && 'text-yellow-500',
            (state === 'idle' || state === 'stopped') && 'text-gray-600 dark:text-gray-400'
          )}>
            {formatDuration(duration)}
          </div>

          {/* Status */}
          <div className="flex items-center space-x-2">
            {state === 'recording' && (
              <>
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm text-red-500 font-medium">Recording</span>
              </>
            )}
            {state === 'paused' && (
              <>
                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                <span className="text-sm text-yellow-500 font-medium">Paused</span>
              </>
            )}
            {state === 'stopped' && audioBlob && (
              <span className="text-sm text-gray-500">Recording ready</span>
            )}
            {state === 'idle' && (
              <span className="text-sm text-gray-500">Click to start recording</span>
            )}
          </div>

          {/* Control Buttons */}
          <div className="flex items-center space-x-3">
            {state === 'idle' && (
              <Button
                size="lg"
                onClick={startRecording}
                disabled={disabled || isUploading}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <Mic className="h-5 w-5 mr-2" />
                Start Recording
              </Button>
            )}

            {state === 'recording' && (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={pauseRecording}
                  className="h-12 w-12"
                >
                  <Pause className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  onClick={stopRecording}
                  className="h-12 w-12 bg-red-500 hover:bg-red-600 text-white"
                >
                  <Square className="h-5 w-5" />
                </Button>
              </>
            )}

            {state === 'paused' && (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={resumeRecording}
                  className="h-12 w-12"
                >
                  <Play className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  onClick={stopRecording}
                  className="h-12 w-12 bg-red-500 hover:bg-red-600 text-white"
                >
                  <Square className="h-5 w-5" />
                </Button>
              </>
            )}

            {state === 'stopped' && audioBlob && (
              <>
                <Button
                  variant="outline"
                  onClick={discardRecording}
                  disabled={isUploading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Discard
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {isUploading ? 'Uploading...' : 'Upload Recording'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Audio Preview */}
      {state === 'stopped' && audioUrl && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview your recording:</p>
          <audio controls src={audioUrl} className="w-full" />
        </div>
      )}
    </div>
  );
}
